import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import productionService, { type CreateProductionSessionRequest, type RawMaterial, type StationAllocation } from '../../services/productionService';
import stationManagementService, { type Operator } from '../../services/stationManagementService';
import { wsService } from '../../services/websocket';
import { useLanguage } from '../../i18n/LanguageContext';
import { DEFAULT_YIELD } from '../../hooks/useYieldFactor';
import { usePageTitle } from '../../contexts/PageTitleContext';
import ManagerScheduleView from './components/ManagerScheduleView';

interface Product {
  sku: string;
  name: string;
  item_chinese_name: string;
  target_weight: string;
  unit_weight: number;
  yield_factor: number;
  category_code: string;
  reference_unit_cost: number;
}

interface RawMaterialEntry {
  item_number: string;
  transfer_number: string;
  item_name: string;
  allocated_weight: string;
  available_stock: number;
}

// Station allocation entry for multi-station mode
interface StationAllocationEntry {
  station_id: number;
  station_name: string;
  materials: {
    item_number: string;
    transfer_number: string;
    allocated_weight: string;
  }[];
}

interface ProductionSessionForm {
  rawMaterialItem: string;  // Legacy - kept for backward compatibility
  productSku: string;
  initialWeight: string;    // Legacy - now calculated from raw materials
  targetOutput: string;
  targetBoxes: string;
  notes: string;
  stationId: string;
  scheduledDate: string;
}


const ProductionPlanning: React.FC = () => {
  const { t } = useLanguage();

  // Set page title in header
  usePageTitle(t('supervisor.productionPlanning'), t('production.title'));

  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get('date');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [showSubSessionOperatorModal, setShowSubSessionOperatorModal] = useState(false);
  const [showSubSessionStationModal, setShowSubSessionStationModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [selectedSubSession, setSelectedSubSession] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showStationModal, setShowStationModal] = useState(false);
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [pendingStationId, setPendingStationId] = useState('');

  // Multi-material form state
  const [rawMaterialEntries, setRawMaterialEntries] = useState<RawMaterialEntry[]>([
    { item_number: '', transfer_number: '', item_name: '', allocated_weight: '', available_stock: 0 }
  ]);

  // Filter states
  const [selectedStation, setSelectedStation] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set());

  // Multi-station mode state (always true - single station mode removed)
  const [multiStationMode] = useState(true);
  const [stationAllocations, setStationAllocations] = useState<StationAllocationEntry[]>([]);

  const [formData, setFormData] = useState<ProductionSessionForm>({
    rawMaterialItem: '',
    productSku: '',
    initialWeight: '',
    targetOutput: '',
    targetBoxes: '',
    notes: '',
    stationId: '',
    scheduledDate: new Date().toLocaleDateString('en-CA')
  });

  // Fetch products from production_inputs table (all valid products)
  const { data: products = [], isLoading: loadingProducts } = useQuery<Product[]>(
    ['products'],
    () => productionService.getProducts(),
    {
      keepPreviousData: true,
      staleTime: 60000,
    }
  );

  // Fetch raw materials linked to selected product via production_inputs
  const { data: rawMaterialsData, isLoading: loadingMaterials } = useQuery(
    ['rawMaterials', formData.productSku],
    () => productionService.getRawMaterials(formData.productSku || undefined),
    {
      enabled: !!formData.productSku, // Only fetch when product is selected
      keepPreviousData: true,
      staleTime: 20000,
    }
  );
  const rawMaterials: RawMaterial[] = rawMaterialsData?.raw_materials || [];

  // Fetch stations (uses stationManagementService which returns StationWithDetails with all fields)
  const { data: stations = [] } = useQuery<any[]>(
    ['stations'],
    () => stationManagementService.getStations(),
    {
      keepPreviousData: true,
      staleTime: 30000,
    }
  );

  // Fetch existing sessions with polling for live progress updates
  const { data: sessions = [], isLoading: loadingSessions } = useQuery<any[]>(
    ['productionSessions'],
    () => productionService.getProductionSessions(),
    {
      staleTime: 5000, // Consider data stale after 5 seconds
      refetchOnMount: 'always', // Always refetch when component mounts (navigation)
      refetchOnWindowFocus: true, // Refetch when tab gains focus
      refetchInterval: 10000, // Poll every 10 seconds for live progress updates
    }
  );

  // Fetch operators for sub-session assignment
  const { data: operators = [] } = useQuery<Operator[]>(
    ['operators'],
    () => stationManagementService.getOperators(),
    {
      enabled: showSubSessionOperatorModal,
    }
  );

  // Mutation for assigning operator to sub-session
  const assignSubSessionOperatorMutation = useMutation(
    (data: { subSessionNumber: string; operatorId: string }) =>
      stationManagementService.assignSubSessionOperator(data.subSessionNumber, data.operatorId),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['productionSessions']);
        setShowSubSessionOperatorModal(false);
        setSelectedSubSession(null);
        toast.success(response.message || 'Operator assigned successfully');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to assign operator');
      },
    }
  );

  // Mutation for assigning station to sub-session
  const assignSubSessionStationMutation = useMutation(
    (data: { subSessionNumber: string; stationId: number }) =>
      stationManagementService.assignSubSessionStation(data.subSessionNumber, data.stationId),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['productionSessions']);
        setShowSubSessionStationModal(false);
        setSelectedSubSession(null);
        toast.success(response.message || 'Station assigned successfully');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to assign station');
      },
    }
  );

  // Clear raw material entries when product changes (since raw materials are product-specific)
  useEffect(() => {
    // Reset raw material entries when product selection changes
    setRawMaterialEntries([
      { item_number: '', transfer_number: '', item_name: '', allocated_weight: '', available_stock: 0 }
    ]);
    // Note: Don't reset targetOutput and targetBoxes here - let them be calculated
    // when raw material weights are entered
  }, [formData.productSku]);

  // Filter sessions based on selected filters
  const filteredSessions = sessions.filter((session: any) => {
    // Station filter
    if (selectedStation && session.station_id?.toString() !== selectedStation) {
      return false;
    }

    // Status filter (includes scheduled/queued)
    if (selectedStatus) {
      if (selectedStatus === 'pending' && session.queue_position) {
        // Show queued sessions
      } else if (selectedStatus === 'scheduled' && session.status === 'received' && session.scheduled_date) {
        // Show scheduled sessions
      } else if (session.status !== selectedStatus) {
        return false;
      }
    }

    // Date range filter
    if (fromDate) {
      const sessionDate = session.scheduled_date?.split('T')[0];
      if (!sessionDate) return false;

      // Check from date
      if (sessionDate < fromDate) return false;

      // Check to date (optional)
      if (toDate && sessionDate > toDate) return false;
    }

    return true;
  });

  // Status options including scheduled/queued
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'received', label: 'Received' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedStation('');
    setSelectedStatus('');
    setFromDate('');
    setToDate('');
  };

  const hasActiveFilters = selectedStation || selectedStatus || fromDate || toDate;

  // Toggle session expansion
  const toggleSession = (sessionId: number) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // WebSocket for real-time updates
  useEffect(() => {
    // Connect to WebSocket
    wsService.connect();

    // Listen for production session events
    const handleSessionCreated = (data: any) => {
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['rawMaterials']);
    };

    const handleSessionUpdated = (data: any) => {
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['stations']); // Also refresh stations when session changes
      queryClient.invalidateQueries(['operators']); // Refresh available operators when session changes
      if (data.action === 'cancelled') {
        queryClient.invalidateQueries(['rawMaterials']);
      }
    };

    const handleStationUpdated = (data: any) => {
      queryClient.invalidateQueries(['stations']);
      queryClient.invalidateQueries(['productionSessions']); // Refresh sessions as station status affects them
    };

    const handleInventoryUpdated = (data: any) => {
      queryClient.invalidateQueries(['rawMaterials']); // Refresh raw materials with new stock levels
    };

    wsService.on('production-session-created', handleSessionCreated);
    wsService.on('production-session-updated', handleSessionUpdated);
    wsService.on('station-updated', handleStationUpdated);
    wsService.on('inventory-updated', handleInventoryUpdated);

    // Cleanup on unmount
    return () => {
      wsService.off('production-session-created', handleSessionCreated);
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('station-updated', handleStationUpdated);
      wsService.off('inventory-updated', handleInventoryUpdated);
    };
  }, [queryClient]);

  // Create session mutation
  const createSessionMutation = useMutation<any, any, CreateProductionSessionRequest>(
    productionService.createProductionSession,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['rawMaterials']);
        queryClient.invalidateQueries(['stations']); // Update Station Management page
        queryClient.invalidateQueries(['supervisor-stations']); // Update Station Management page
        setShowCreateForm(false);
        resetForm();
        setValidationErrors([]);
        toast.success('Production session created successfully');
      },
      onError: (error: any) => {
        if (error.response?.data?.errors) {
          setValidationErrors(error.response.data.errors);
        }
      }
    }
  );

  // Cancel session mutation
  const cancelSessionMutation = useMutation(
    (data: { sessionId: string; reason: string }) =>
      productionService.cancelSession(data.sessionId, data.reason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['rawMaterials']);
        queryClient.invalidateQueries(['stations']); // Update Station Management page
        queryClient.invalidateQueries(['supervisor-stations']); // Update Station Management page
        setShowCancelModal(false);
        setSelectedSession(null);
        setCancelReason('');
        toast.success('Production session cancelled successfully');
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to cancel session';
        toast.error(errorMessage);
      }
    }
  );

  // Queue session mutation
  const queueSessionMutation = useMutation(
    (data: { sessionId: string; stationId: number }) =>
      productionService.queueSession(data.sessionId, data.stationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productionSessions']);
        setShowQueueModal(false);
        setSelectedSession(null);
        toast.success('Session added to pending queue successfully');
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || 'Failed to queue session';
        toast.error(errorMessage);
      }
    }
  );

  // Remove from queue mutation
  const removeFromQueueMutation = useMutation(
    (sessionId: string) => productionService.removeFromQueue(sessionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productionSessions']);
        toast.success('Session removed from queue');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Failed to remove from queue');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      rawMaterialItem: '',
      productSku: '',
      initialWeight: '',
      targetOutput: '',
      targetBoxes: '',
      notes: '',
      stationId: '',
      scheduledDate: new Date().toLocaleDateString('en-CA')
    });
    // Reset raw material entries to single empty entry
    setRawMaterialEntries([
      { item_number: '', transfer_number: '', item_name: '', allocated_weight: '', available_stock: 0 }
    ]);
    // Reset station allocations
    setStationAllocations([]);
  };

  const handleInputChange = (field: keyof ProductionSessionForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Note: Don't calculate target output here when product changes
    // because raw materials get reset to empty. The calculation will
    // trigger automatically when raw material weights are entered.
  };

  // Multi-material helper functions
  const addRawMaterial = () => {
    setRawMaterialEntries(prev => [
      ...prev,
      { item_number: '', transfer_number: '', item_name: '', allocated_weight: '', available_stock: 0 }
    ]);
  };

  const removeRawMaterial = (index: number) => {
    if (rawMaterialEntries.length <= 1) {
      // Keep at least one entry
      setRawMaterialEntries([{ item_number: '', transfer_number: '', item_name: '', allocated_weight: '', available_stock: 0 }]);
    } else {
      setRawMaterialEntries(prev => prev.filter((_, i) => i !== index));
    }
    // Recalculate target output
    setTimeout(() => {
      const totalWeight = calculateTotalMaterialWeight();
      calculateTargetOutput(formData.productSku, totalWeight.toString());
    }, 0);
  };

  const updateRawMaterial = (index: number, field: keyof RawMaterialEntry, value: string | number) => {
    setRawMaterialEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // If item_number changed (lot selection), update related fields
      // The value format is "item_number|transfer_number" for lot selection
      if (field === 'item_number' && typeof value === 'string' && value.includes('|')) {
        const [itemNumber, transferNumber] = value.split('|');
        const selectedMaterial = rawMaterials.find(
          rm => rm.item_number === itemNumber && rm.transfer_number === transferNumber
        );
        if (selectedMaterial) {
          updated[index].item_number = itemNumber;
          updated[index].transfer_number = transferNumber;
          updated[index].item_name = selectedMaterial.item_english_name;
          updated[index].available_stock = selectedMaterial.current_stock;
        } else {
          updated[index].item_number = '';
          updated[index].transfer_number = '';
          updated[index].item_name = '';
          updated[index].available_stock = 0;
        }
      } else if (field === 'item_number') {
        // Clear selection if no valid lot selected
        updated[index].item_number = '';
        updated[index].transfer_number = '';
        updated[index].item_name = '';
        updated[index].available_stock = 0;
      }

      // Recalculate target output when weight changes - use updated array directly
      // to avoid stale state issue (reading old state before update completes)
      if (field === 'allocated_weight') {
        const totalWeight = updated.reduce((sum, entry) => {
          return sum + (parseFloat(entry.allocated_weight) || 0);
        }, 0);
        setTimeout(() => {
          calculateTargetOutput(formData.productSku, totalWeight.toString());
        }, 0);
      }

      return updated;
    });
  };

  const calculateTotalMaterialWeight = (): number => {
    return rawMaterialEntries.reduce((sum, entry) => {
      const weight = parseFloat(entry.allocated_weight) || 0;
      return sum + weight;
    }, 0);
  };

  // Find available remnants for FIFO hint (when selecting non-remnant material)
  const findAvailableRemnants = (selectedMaterial: RawMaterial | undefined): RawMaterial[] => {
    if (!selectedMaterial || selectedMaterial.is_remnant) return [];
    return rawMaterials.filter(m =>
      m.is_remnant &&
      m.base_item_number === selectedMaterial.item_number &&
      m.is_available
    );
  };

  // Multi-station allocation helpers
  const addStationAllocation = () => {
    // Initialize with empty materials for each selected raw material
    const initialMaterials = rawMaterialEntries
      .filter(e => e.item_number && e.allocated_weight)
      .map(e => ({
        item_number: e.item_number,
        transfer_number: e.transfer_number,
        allocated_weight: ''
      }));

    setStationAllocations(prev => [
      ...prev,
      { station_id: 0, station_name: '', materials: initialMaterials }
    ]);
  };

  const removeStationAllocation = (index: number) => {
    setStationAllocations(prev => prev.filter((_, i) => i !== index));
  };

  const updateStationAllocation = (index: number, stationId: string) => {
    const station = stations.find((s: any) => s.station_id.toString() === stationId);
    setStationAllocations(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        station_id: Number(stationId),
        station_name: station?.station_name || ''
      };
      return updated;
    });
  };

  const updateStationMaterialWeight = (
    stationIndex: number,
    materialIndex: number,
    weight: string
  ) => {
    setStationAllocations(prev => {
      const updated = [...prev];
      updated[stationIndex] = {
        ...updated[stationIndex],
        materials: updated[stationIndex].materials.map((m, i) =>
          i === materialIndex ? { ...m, allocated_weight: weight } : m
        )
      };
      return updated;
    });
  };

  // Calculate unallocated weight for each material across all station allocations
  const getUnallocatedWeight = (itemNumber: string, transferNumber: string): number => {
    const material = rawMaterialEntries.find(
      e => e.item_number === itemNumber && e.transfer_number === transferNumber
    );
    if (!material) return 0;

    const totalWeight = parseFloat(material.allocated_weight) || 0;
    const allocatedWeight = stationAllocations.reduce((sum, station) => {
      const stationMaterial = station.materials.find(
        m => m.item_number === itemNumber && m.transfer_number === transferNumber
      );
      return sum + (parseFloat(stationMaterial?.allocated_weight || '0') || 0);
    }, 0);

    return totalWeight - allocatedWeight;
  };

  // Check if all material is allocated across stations
  const isFullyAllocated = (): boolean => {
    const validEntries = rawMaterialEntries.filter(e => e.item_number && e.allocated_weight);
    return validEntries.every(entry => {
      const unallocated = getUnallocatedWeight(entry.item_number, entry.transfer_number);
      return Math.abs(unallocated) < 0.01; // Allow small floating point tolerance
    });
  };

  // Sync station allocations when raw materials change
  useEffect(() => {
    if (multiStationMode && stationAllocations.length > 0) {
      const validEntries = rawMaterialEntries.filter(e => e.item_number && e.allocated_weight);

      setStationAllocations(prev =>
        prev.map(station => ({
          ...station,
          materials: validEntries.map(entry => {
            const existing = station.materials.find(
              m => m.item_number === entry.item_number && m.transfer_number === entry.transfer_number
            );
            return existing || {
              item_number: entry.item_number,
              transfer_number: entry.transfer_number,
              allocated_weight: ''
            };
          })
        }))
      );
    }
  }, [rawMaterialEntries, multiStationMode]);

  // Parse yield factor — backend returns decimal (e.g. 0.85), use directly
  const normalizeYieldFactor = (yieldFactor: number | string): number => {
    const value = typeof yieldFactor === 'string' ? parseFloat(yieldFactor) : yieldFactor;
    if (isNaN(value)) return 0;
    return value;
  };

  const calculateTargetOutput = (sku: string, weight: string) => {
    if (!sku || !weight) return;

    const product = products.find((p: Product) => p.sku === sku);
    if (!product) return;

    const initialWeight = parseFloat(weight);
    if (isNaN(initialWeight) || initialWeight <= 0) return;

    // Normalize yield factor to decimal format, default to 85% if missing or 0
    const rawYieldFactor = product.yield_factor ? normalizeYieldFactor(product.yield_factor) : 0;
    const yieldFactor = rawYieldFactor > 0 ? rawYieldFactor : DEFAULT_YIELD;

    // Calculate expected output weight after yield loss
    const expectedOutputWeight = initialWeight * yieldFactor;

    // Use unit_weight directly - it's the total box weight (e.g., 40 for 4x10lb product)
    const boxWeight = Number(product.unit_weight) || 0;

    // Set estimated weight
    const targetOutput = expectedOutputWeight.toFixed(2);

    // Calculate number of whole boxes that can be produced
    const targetBoxes = boxWeight > 0 ? Math.floor(expectedOutputWeight / boxWeight).toString() : '';

    setFormData(prev => ({ ...prev, targetOutput, targetBoxes }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);

    // Client-side validation
    const errors: string[] = [];

    // Validate raw materials
    const validEntries = rawMaterialEntries.filter(e => e.item_number && e.allocated_weight);
    if (validEntries.length === 0) {
      errors.push('At least one raw material with weight is required');
    }

    // Check for duplicate materials (same item + same lot)
    const lotKeys = validEntries.map(e => `${e.item_number}|${e.transfer_number}`);
    const duplicates = lotKeys.filter((key, idx) => lotKeys.indexOf(key) !== idx);
    if (duplicates.length > 0) {
      const duplicateItems = [...new Set(duplicates)].map(k => k.split('|')[0]);
      errors.push(`Duplicate raw materials selected: ${duplicateItems.join(', ')}`);
    }

    // Validate each entry
    for (const entry of validEntries) {
      const weight = parseFloat(entry.allocated_weight);
      if (isNaN(weight) || weight <= 0) {
        errors.push(`Invalid weight for ${entry.item_name || entry.item_number}`);
        continue;
      }
      if (weight > entry.available_stock) {
        const material = rawMaterials.find(rm => rm.item_number === entry.item_number);
        errors.push(`Insufficient stock for ${material?.item_english_name || entry.item_number}: requested ${weight.toFixed(2)} lbs, available ${entry.available_stock.toFixed(2)} lbs`);
      }
    }

    if (!formData.productSku) errors.push('Product SKU is required');

    const totalWeight = calculateTotalMaterialWeight();
    if (totalWeight <= 0) {
      errors.push('Total material weight must be greater than 0');
    }

    // Multi-station validation
    if (multiStationMode) {
      if (stationAllocations.length === 0) {
        errors.push('At least one station must be allocated in multi-station mode');
      }

      // Check for duplicate stations
      const stationIds = stationAllocations.map(s => s.station_id).filter(id => id);
      const duplicateStations = stationIds.filter((id, idx) => stationIds.indexOf(id) !== idx);
      if (duplicateStations.length > 0) {
        errors.push('Each station can only be assigned once');
      }

      // Check that all stations have been selected
      const unselectedStations = stationAllocations.filter(s => !s.station_id);
      if (unselectedStations.length > 0) {
        errors.push('Please select a station for all allocations');
      }

      // Validate that all material is fully allocated
      if (!isFullyAllocated()) {
        const unallocatedMaterials = validEntries.filter(entry => {
          const unallocated = getUnallocatedWeight(entry.item_number, entry.transfer_number);
          return Math.abs(unallocated) >= 0.01;
        });
        if (unallocatedMaterials.length > 0) {
          errors.push('All material weight must be allocated to stations. Check allocation totals.');
        }
      }

      // Validate that each station has at least some material
      const emptyStations = stationAllocations.filter(station => {
        const totalStationWeight = station.materials.reduce(
          (sum, m) => sum + (parseFloat(m.allocated_weight) || 0), 0
        );
        return totalStationWeight <= 0;
      });
      if (emptyStations.length > 0) {
        errors.push('Each station must have at least some material allocated');
      }
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Build request
    const request: CreateProductionSessionRequest = {
      raw_materials: validEntries.map(e => ({
        item_number: e.item_number,
        transfer_number: e.transfer_number,
        allocated_weight: parseFloat(e.allocated_weight)
      })),
      product_sku: formData.productSku,
      target_output_weight: parseFloat(formData.targetOutput) || totalWeight * DEFAULT_YIELD,
      notes: formData.notes,
      scheduled_date: formData.scheduledDate || undefined
    };

    // Add station assignment based on mode
    if (multiStationMode && stationAllocations.length > 0) {
      // Multi-station mode: include station_allocations
      request.station_allocations = stationAllocations.map(allocation => ({
        station_id: allocation.station_id,
        materials: allocation.materials
          .filter(m => parseFloat(m.allocated_weight) > 0)
          .map(m => ({
            item_number: m.item_number,
            transfer_number: m.transfer_number,
            allocated_weight: parseFloat(m.allocated_weight)
          }))
      }));
    } else if (formData.stationId) {
      // Single station mode (legacy)
      request.station_id = parseInt(formData.stationId);
    }

    createSessionMutation.mutate(request);
  };


  const canCancelSession = (session: any) => {
    // Allow cancellation if:
    // 1. No station is assigned, OR
    // 2. Station is assigned but inactive
    if (!session.station_id) {
      return { canCancel: true, reason: '' };
    }

    const assignedStation = stations.find((s: any) => s.station_id === session.station_id);
    if (!assignedStation) {
      return { canCancel: true, reason: '' };
    }

    // Check if station is inactive (not 'active' or 'idle')
    const isStationInactive = assignedStation.status !== 'active' && assignedStation.status !== 'idle';
    if (isStationInactive) {
      return { canCancel: true, reason: '' };
    }

    return {
      canCancel: false,
      reason: `Cannot cancel: Session is assigned to active station "${assignedStation.station_name}" (${assignedStation.status})`
    };
  };

  const handleCancelSession = (session: any) => {
    const { canCancel, reason } = canCancelSession(session);

    if (!canCancel) {
      toast.error(reason, { duration: 4000 });
      return;
    }

    setSelectedSession(session);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (selectedSession && cancelReason.trim()) {
      cancelSessionMutation.mutate({
        sessionId: selectedSession.session_number,
        reason: cancelReason
      });
    }
  };

  const handleQueueSession = (session: any) => {
    setSelectedSession(session);
    setShowQueueModal(true);
  };

  const handleConfirmQueue = (stationId: string) => {
    if (selectedSession && stationId) {
      queueSessionMutation.mutate({
        sessionId: selectedSession.session_number,
        stationId: parseInt(stationId)
      });
    }
  };

  const handleRemoveFromQueue = (sessionId: string) => {
    removeFromQueueMutation.mutate(sessionId);
  };

  const handleAssignSubSessionOperator = (subSession: any) => {
    setSelectedSubSession(subSession);
    setShowSubSessionOperatorModal(true);
  };

  const handleConfirmSubSessionOperator = (operatorId: string) => {
    if (selectedSubSession && operatorId) {
      assignSubSessionOperatorMutation.mutate({
        subSessionNumber: selectedSubSession.sub_session_number,
        operatorId
      });
    }
  };

  const handleAssignSubSessionStation = (subSession: any) => {
    setSelectedSubSession(subSession);
    setShowSubSessionStationModal(true);
  };

  const handleConfirmSubSessionStation = (stationId: string) => {
    if (selectedSubSession && stationId) {
      assignSubSessionStationMutation.mutate({
        subSessionNumber: selectedSubSession.sub_session_number,
        stationId: parseInt(stationId)
      });
    }
  };

  // Handle station assignment for session
  const handleStationAssignment = (stationId: string) => {
    if (selectedSession && stationId) {
      stationManagementService.assignSession(parseInt(stationId), selectedSession.session_number)
        .then(() => {
          toast.success('Station assigned successfully');
          queryClient.invalidateQueries(['productionSessions']);
          setShowStationModal(false);
          setSelectedSession(null);
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to assign station');
        });
    }
  };

  // Handle operator assignment for session
  const handleOperatorAssignment = (operatorId: string) => {
    if (selectedSession && operatorId) {
      stationManagementService.assignOperator(selectedSession.station_id, operatorId)
        .then(() => {
          toast.success('Operator assigned successfully');
          queryClient.invalidateQueries(['productionSessions']);
          setShowOperatorModal(false);
          setSelectedSession(null);
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to assign operator');
        });
    }
  };

  const selectedProduct = products.find((p: Product) => p.sku === formData.productSku);

  return (
    <div data-tour="session-list" className="space-y-6">
      <ManagerScheduleView initialDate={initialDate} />
    </div>
  );
};

export default ProductionPlanning;
